import React, { useEffect, useState } from "react";
import { trackFeatured, SRC_PLACEMENT } from "@/lib/featuredPro";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { PublicHeader } from "@/components/PublicHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { BuyerNeedCard } from "@/components/BuyerNeedCard";
import { Button } from "@/components/ui/button";
import { api, apiError, priceLabel } from "@/lib/api";
import { proShareUrl, publicSiteUrl, webShareOrFallback, copyToClipboard, canNativeShare, openProShare } from "@/lib/share";
import { ShareMenu } from "@/components/ShareMenu";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  BadgeCheck, MapPin, Building2, Loader2, Users, Home, Share2, Link2,
  UserPlus, Check, Clock, Mail, Phone, PhoneCall, MessageCircle, Settings, Briefcase, Handshake, Send, Sparkles,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { telHref, waHref, viberHref } from "@/lib/contact";
import { Seo } from "@/components/Seo";
import { ProInquiryDialog } from "@/components/ProInquiryDialog";
import { ReferClientModal } from "@/components/ReferClientModal";
import { readReferralResume, clearReferralResume } from "@/lib/referralResume";

const GOLD = "#C79A3B";
const PROPS_PAGE = 12;

function PropertyMini({ p }) {
  return (
    <Link to={`/listing/${p.id}`} data-testid={`pro-property-${p.id}`}
      className="group block bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-[#0F2C59] hover:shadow-md transition-all">
      <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
        <img src={p.image || p.cover_image} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500" />
      </div>
      <div className="p-4">
        <div className="font-display text-lg font-bold text-slate-900">{p.listing_type === "Unit Hosting" ? `₱${(p.nightly_rate || 0).toLocaleString()} / night` : priceLabel(p)}</div>
        <h3 className="text-sm font-semibold text-slate-800 mt-1 line-clamp-2 min-h-[2.5rem]">{p.title}</h3>
        <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><MapPin className="h-3 w-3 shrink-0" />{p.city || "Philippines"}{p.project ? ` · ${p.project}` : ""}</p>
      </div>
    </Link>
  );
}

function Empty({ icon: Icon, text }) {
  return (
    <div className="text-center py-12 border border-dashed border-slate-200 rounded-xl bg-slate-50/50">
      <Icon className="h-8 w-8 text-slate-300 mx-auto" />
      <p className="text-sm text-slate-500 mt-2">{text}</p>
    </div>
  );
}

// Direct-contact channels — renders ONLY the opt-in public mobile (Call/WhatsApp/Viber) and
// public email. Disabled/absent channels are simply not rendered (never a dead end upstream).
function ContactChannels({ pro }) {
  const hasMobile = !!pro.public_mobile;
  const hasEmail = !!pro.public_email;
  if (!hasMobile && !hasEmail) return null;
  const tel = telHref(pro.public_mobile);
  const wa = waHref(pro.public_mobile);
  const viber = viberHref(pro.public_mobile);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="pro-contact-channels">
      {hasMobile && tel && (
        <a href={tel} data-testid="pro-contact-call" className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#0F2C59] text-white font-semibold text-sm px-4 py-2.5 hover:bg-[#0F2C59]/90 transition-colors">
          <PhoneCall className="h-4 w-4" /> Call
        </a>
      )}
      {hasMobile && wa && (
        <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="pro-contact-whatsapp" className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#0F2C59] text-[#0F2C59] font-semibold text-sm px-4 py-2.5 hover:bg-[#0F2C59]/5 transition-colors">
          <MessageCircle className="h-4 w-4" /> WhatsApp
        </a>
      )}
      {hasMobile && viber && (
        <a href={viber} data-testid="pro-contact-viber" className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#0F2C59] text-[#0F2C59] font-semibold text-sm px-4 py-2.5 hover:bg-[#0F2C59]/5 transition-colors">
          <Phone className="h-4 w-4" /> Viber
        </a>
      )}
      {hasEmail && (
        <a href={`mailto:${pro.public_email}?subject=Inquiry via PropertyConnect`} data-testid="pro-contact-email" className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#C79A3B] text-[#a97f28] font-semibold text-sm px-4 py-2.5 hover:bg-[#C79A3B]/5 transition-colors">
          <Mail className="h-4 w-4" /> Email
        </a>
      )}
    </div>
  );
}

export default function PublicProfile() {
  const { slug } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const { user } = useAuth();
  const [state, setState] = useState("loading"); // loading | ok | notfound
  const [pro, setPro] = useState(null);
  const [props, setProps] = useState({ total: 0, items: [] });
  const [needs, setNeeds] = useState({ total: 0, items: [] });
  const [internalId, setInternalId] = useState(null);
  const [connState, setConnState] = useState(null); // relationship status label
  const [connecting, setConnecting] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [workOpen, setWorkOpen] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  // Featured Professionals attribution (?src=featured_home|spotlight&cid=...).
  const qs = new URLSearchParams(loc.search);
  const featCid = qs.get("cid");
  const featPlacement = SRC_PLACEMENT[qs.get("src")];
  const { config } = useAuth();
  const inquiryOn = !!config?.featured_professional_enabled;
  const wantsInquiry = qs.get("inquire") === "1" && inquiryOn;

  useEffect(() => {
    let alive = true;
    setState("loading");
    (async () => {
      try {
        const { data } = await api.get(`/public/professionals/${slug}`);
        if (!alive) return;
        setPro(data);
        setState("ok");
        api.get(`/public/professionals/${slug}/properties`, { params: { limit: PROPS_PAGE } }).then((r) => alive && setProps(r.data)).catch(() => {});
        api.get(`/public/professionals/${slug}/buyer-needs`).then((r) => alive && setNeeds(r.data)).catch(() => {});
      } catch (e) {
        if (alive) setState("notfound");
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  // Deep link from featured cards ("Explore N Properties") → scroll to the listings section.
  useEffect(() => {
    if (state !== "ok" || loc.hash !== "#properties") return;
    const t = setTimeout(() => document.getElementById("properties")?.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
    return () => clearTimeout(t);
  }, [state, loc.hash]);

  const [moreBusy, setMoreBusy] = useState(false);
  const loadMoreProps = async () => {
    setMoreBusy(true);
    try {
      const { data } = await api.get(`/public/professionals/${slug}/properties`, { params: { limit: PROPS_PAGE, skip: props.items.length } });
      setProps((cur) => ({ total: data.total, items: [...cur.items, ...data.items.filter((x) => !cur.items.some((y) => y.id === x.id))] }));
    } catch { /* keep current list */ }
    finally { setMoreBusy(false); }
  };

  useEffect(() => {
    if (state !== "ok") return;
    if (featCid && featPlacement) trackFeatured("profile_view", featCid, featPlacement);
    if (wantsInquiry) setInquiryOpen(true);
  }, [state]); // eslint-disable-line

  // Authenticated viewers: resolve internal id (never exposed publicly) to enable Connect + state.
  useEffect(() => {
    if (state !== "ok" || !user || !pro?.public_id) return;
    let alive = true;
    api.get(`/professionals/by-public-id/${pro.public_id}`).then(async ({ data }) => {
      if (!alive) return;
      setInternalId(data.id);
      if (data.id === user.id) return; // own profile
      try {
        const { data: card } = await api.get(`/connections/professional/${data.id}`);
        if (alive) setConnState(card.connection_state || null);
      } catch { /* not eligible for connections — leave null */ }
    }).catch(() => {});
    return () => { alive = false; };
  }, [state, user, pro]);

  // Public-safe professional social metadata injected into the document head at render time
  // (same pattern as PublicListingDetail — the prerender/crawler layer captures these tags).
  // Values mirror the backend /api/og/pro/{slug} exactly so there is a single source of truth.
  useEffect(() => {
    if (!pro) return;
    const role = pro.sales_position_label || pro.role_label;
    const title = role ? `${pro.name} — ${role} on PropertyConnect` : `${pro.name} on PropertyConnect`;
    const desc = `View ${pro.name}'s professional profile, properties and opportunities on PropertyConnect.`;
    const url = proShareUrl(pro.public_slug || slug);
    // og:image = the dynamically composed 1200x630 professional identity card (single source of
    // truth with the backend). Versioned so socials re-scrape after profile edits.
    const ver = pro.og_image_version ? `?v=${pro.og_image_version}` : "";
    const img = `${publicSiteUrl()}/api/og/pro/${pro.public_slug || slug}/card.png${ver}`;
    const prevTitle = document.title;
    document.title = `${title}`;
    const set = (key, val, prop = true) => {
      if (!val) return;
      const a = prop ? "property" : "name";
      let el = document.head.querySelector(`meta[${a}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(a, key); document.head.appendChild(el); }
      el.setAttribute("content", val);
    };
    set("description", desc, false);
    set("og:site_name", "PropertyConnect");
    set("og:title", title);
    set("og:description", desc);
    set("og:image", img);
    set("og:type", "profile");
    set("og:url", url);
    set("twitter:card", "summary_large_image", false);
    set("twitter:title", title, false);
    set("twitter:description", desc, false);
    set("twitter:image", img, false);
    let canon = document.head.querySelector('link[rel="canonical"]');
    const hadCanon = !!canon; const prevCanon = canon && canon.getAttribute("href");
    if (!canon) { canon = document.createElement("link"); canon.setAttribute("rel", "canonical"); document.head.appendChild(canon); }
    canon.setAttribute("href", url);
    return () => { document.title = prevTitle; if (hadCanon) canon.setAttribute("href", prevCanon || ""); else canon.remove(); };
  }, [pro, slug]);

  const isOwner = user && internalId && internalId === user.id;
  // CP5: professional viewers may refer a client to another eligible professional (not self,
  // not consumers/guests/partners). Backend remains authoritative on eligibility.
  const canRefer = !!user && !!internalId && !isOwner && !["CONSUMER", "REFERRAL_PARTNER"].includes(user.account_type);
  const [refer, setRefer] = useState({ open: false, needId: null });
  useEffect(() => {
    const r = readReferralResume();
    if (r?.recipient && internalId && r.recipient.id === internalId) { setRefer({ open: true, needId: r.needId || null }); clearReferralResume(); }
  }, [internalId]);
  const referRecipient = { id: internalId, name: pro?.name, avatar: pro?.avatar_url || pro?.avatar, role_label: pro?.role_label, organization_name: pro?.organization_name, city: pro?.city };

  const doShare = () => openProShare({
    url: proShareUrl(pro.public_slug || slug),
    name: pro.name,
    position: pro.sales_position_label || pro.role_label,
    openMenu: () => setShareOpen(true),
  });
  const doCopy = async () => {
    const ok = await copyToClipboard(proShareUrl(pro.public_slug || slug));
    toast(ok ? "Profile link copied" : "Copy failed — long-press to copy");
  };
  const doConnect = async () => {
    if (connState === "RESPOND") { nav("/connections?tab=requests"); return; }
    if (!user) { nav("/login", { state: { from: loc.pathname } }); return; }
    if (!internalId) { toast("This professional isn't available for connections right now."); return; }
    setConnecting(true);
    try {
      await api.post(`/connections/request`, { recipient_id: internalId });
      setConnState("REQUEST_SENT");
      toast("Connection request sent");
      if (featCid && featPlacement) trackFeatured("connection_request", featCid, featPlacement);
    } catch (e) { toast(apiError(e)); }
    finally { setConnecting(false); }
  };
  const doInquiry = () => setWorkOpen(true);

  if (state === "loading") return (
    <div className="min-h-screen bg-white"><PublicHeader />
      <div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
    </div>
  );
  if (state === "notfound") return (
    <div className="min-h-screen bg-white flex flex-col"><PublicHeader />
      <Seo robots="noindex,follow" />
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-24 text-center" data-testid="pro-notfound">
        <Users className="h-12 w-12 text-slate-300" />
        <h1 className="font-display text-2xl font-bold text-[#0F2C59] mt-4">Profile not available</h1>
        <p className="text-slate-500 mt-2 max-w-md">This professional profile doesn't exist or hasn't been published.</p>
        <Button onClick={() => nav("/marketplace")} className="mt-6 rounded-xl bg-[#C79A3B] hover:bg-[#C79A3B]/90 text-white font-semibold">Explore the Marketplace</Button>
      </div>
      <SiteFooter variant="full" />
    </div>
  );

  const areas = pro.service_areas || [];
  const featured = !!pro.is_featured;
  const connLabel = connState === "CONNECTED" ? "Connected" : connState === "REQUEST_SENT" ? "Pending" : connState === "RESPOND" ? "Respond" : "Connect";
  const ConnIcon = connState === "CONNECTED" ? Check : connState === "REQUEST_SENT" ? Clock : UserPlus;
  const connDone = connState === "CONNECTED" || connState === "REQUEST_SENT";
  const hasMobile = !!pro.public_mobile;
  const hasEmail = !!pro.public_email;
  const hasChannels = hasMobile || hasEmail;

  const personJsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: pro.name,
    ...(pro.sales_position_label || pro.role_label ? { jobTitle: pro.sales_position_label || pro.role_label } : {}),
    ...(pro.organization_name || pro.agency ? { worksFor: { "@type": "Organization", name: pro.organization_name || pro.agency } } : {}),
    ...(pro.avatar ? { image: pro.avatar } : {}),
    ...(areas.length ? { areaServed: areas } : {}),
    url: proShareUrl(pro.public_slug || slug),
  };

  return (
    <div className="min-h-screen bg-[#F7F9FC] flex flex-col" data-testid="public-profile">
      <Seo robots="index,follow" jsonLd={personJsonLd} />
      <PublicHeader />

      {/* HERO */}
      <section className={`relative bg-[#0F2C59] text-white ${featured ? "border-b-2 border-[#C79A3B]" : ""}`} data-testid={featured ? "pro-hero-featured" : "pro-hero"}>
        {featured && <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#C79A3B]/0 via-[#C79A3B] to-[#C79A3B]/0" aria-hidden="true" />}
        <div className={`max-w-5xl mx-auto px-5 sm:px-6 ${featured ? "py-10 sm:py-12" : "py-8 sm:py-10"}`}>
          {featured && (
            <div className="mb-5 flex flex-wrap items-center gap-2">
              <span data-testid="pro-featured-label" className="inline-flex items-center gap-1.5 rounded-md bg-[#C79A3B]/15 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[#E6C878] ring-1 ring-[#C79A3B]/50">
                <Sparkles className="h-3.5 w-3.5" /> Featured Professional
              </span>
              <span className="text-[11px] text-white/50">Promoted placement · does not indicate verification or credentials</span>
            </div>
          )}
          <div className="flex flex-col sm:flex-row sm:items-start gap-5">
            <img src={pro.avatar || "/brand/avatar-fallback.png"} alt={pro.name}
              className={`${featured ? "h-28 w-28 sm:h-36 sm:w-36 ring-2 ring-[#C79A3B] ring-offset-4 ring-offset-[#0F2C59]" : "h-24 w-24 sm:h-28 sm:w-28 border border-white/15"} rounded-2xl object-cover object-top bg-white/10 shrink-0`}
              data-testid="pro-avatar" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className={`font-display font-bold ${featured ? "text-3xl sm:text-4xl" : "text-2xl sm:text-3xl"}`} data-testid="pro-name">{pro.name}</h1>
                {pro.is_verified && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#1D63D8] text-white" data-testid="pro-verified">
                    <BadgeCheck className="h-3.5 w-3.5" /> Verified Professional
                  </span>
                )}
              </div>
              {pro.professional_headline && <p className="text-[#E8C878] mt-1 text-sm font-medium" data-testid="pro-headline">{pro.professional_headline}</p>}
              <p className="text-white/90 mt-1.5 text-sm font-semibold">{pro.sales_position_label || pro.role_label}</p>
              {pro.sales_position_label && pro.role_label && <p className="text-white/60 text-sm">{pro.role_label}</p>}
              {(pro.organization_name || pro.agency) && (
                <p className="text-white/80 text-sm mt-1 flex items-center gap-1.5"><Building2 className="h-4 w-4 shrink-0 text-white/50" />
                  {pro.organization_name || pro.agency}{pro.organization_branch ? ` — ${pro.organization_branch}` : ""}</p>
              )}
              {pro.city && <p className="text-white/70 text-sm mt-1 flex items-center gap-1.5"><MapPin className="h-4 w-4 shrink-0 text-white/50" />{pro.city}</p>}
              {areas.length > 0 && (
                <div className="mt-3">
                  <p className="text-[11px] uppercase tracking-wide text-white/50 mb-1.5">Areas Served</p>
                  <div className="flex flex-wrap gap-1.5" data-testid="pro-areas">
                    {areas.map((a) => <span key={a} className="text-xs bg-white/10 border border-white/15 text-white/90 px-2 py-0.5 rounded-full">{a}</span>)}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* PRIMARY ACTIONS */}
          <div className="flex flex-wrap gap-2.5 mt-6">
            {isOwner ? (
              <Button onClick={() => nav("/profile")} data-testid="pro-manage" className="rounded-xl bg-white text-[#0F2C59] hover:bg-white/90 font-semibold"><Settings className="h-4 w-4 mr-1.5" /> Manage Public Profile</Button>
            ) : (
              <>
                {inquiryOn && <Button onClick={() => setInquiryOpen(true)} data-testid="pro-send-inquiry"
                  className="rounded-xl bg-[#C79A3B] hover:bg-[#b98d33] text-[#0F2C59] font-semibold"><Send className="h-4 w-4 mr-1.5" /> Send Inquiry</Button>}
                <Button onClick={doConnect} disabled={connecting || connDone} data-testid="pro-connect"
                  className={`rounded-xl font-semibold disabled:opacity-70 ${inquiryOn ? "bg-white text-[#0F2C59] hover:bg-white/90" : "bg-[#C79A3B] hover:bg-[#C79A3B]/90 text-white"}`}>
                  <ConnIcon className="h-4 w-4 mr-1.5" /> {connecting ? "Sending…" : connLabel}
                </Button>
                <Button onClick={doInquiry} data-testid="pro-inquiry" variant="outline" className="rounded-xl border-white/30 bg-white/5 text-white hover:bg-white/15 hover:text-white font-semibold"><Briefcase className="h-4 w-4 mr-1.5" /> Work With Me</Button>
                {canRefer && <Button onClick={() => setRefer({ open: true, needId: null })} data-testid="pro-refer-client" className="rounded-xl bg-white text-[#0F2C59] hover:bg-white/90 font-semibold"><Handshake className="h-4 w-4 mr-1.5" /> Refer Client</Button>}
              </>
            )}
            <Button onClick={doShare} data-testid="pro-share" variant="outline" className="rounded-xl border-white/30 bg-white/5 text-white hover:bg-white/15 hover:text-white font-semibold"><Share2 className="h-4 w-4 mr-1.5" /> Share</Button>
          </div>
          {canRefer && <ReferClientModal recipient={referRecipient} initialNeedId={refer.needId} open={refer.open} entrySurface="PROFILE"
            onOpenChange={(o) => setRefer((s) => ({ ...s, open: o }))}
            onCreated={() => { setRefer({ open: false, needId: null }); nav("/referrals?tab=sent"); }} />}
        </div>
      </section>

      <main className="flex-1 max-w-5xl w-full mx-auto px-5 sm:px-6 py-8 space-y-10">
        {featured && <ProfileHighlights pro={pro} areas={areas} />}
        {/* OVERVIEW */}
        <section data-testid="section-overview">
          <h2 className="font-display text-lg font-bold text-[#0F2C59]">Overview</h2>
          <div className="mt-3 bg-white border border-slate-200 rounded-2xl p-5">
            {pro.bio ? <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line" data-testid="pro-bio">{pro.bio}</p>
              : <p className="text-sm text-slate-400 italic">No bio provided yet.</p>}
            {pro.specialties?.length > 0 && (
              <div className="mt-4">
                <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">Areas of Expertise</p>
                <div className="flex flex-wrap gap-1.5" data-testid="pro-specialties">
                  {pro.specialties.map((s) => <span key={s.code} className="text-xs font-medium bg-[#0F2C59]/[0.06] text-[#1F3A63] border border-[#0F2C59]/10 px-2.5 py-1 rounded-full">{s.label}</span>)}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* PROPERTIES */}
        <section id="properties" data-testid="section-properties" className="scroll-mt-24">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-bold text-[#0F2C59]">Active Properties {props.total > 0 && <span data-testid="pro-properties-total" className="text-slate-400 font-medium">({props.total})</span>}</h2>
            {props.total > 0 && <span data-testid="pro-properties-shown" className="text-xs text-slate-500">Showing {props.items.length} of {props.total}</span>}
          </div>
          {props.items?.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-3">
                {props.items.map((p) => <PropertyMini key={p.id} p={p} />)}
              </div>
              {props.total > props.items.length && (
                <div className="mt-5 flex justify-center">
                  <Button data-testid="pro-properties-load-more" variant="outline" disabled={moreBusy} onClick={loadMoreProps}
                    className="rounded-xl border-[#0F2C59]/25 text-[#0F2C59]">
                    {moreBusy ? "Loading…" : `Show more properties (${props.total - props.items.length} more)`}
                  </Button>
                </div>
              )}
            </>
          ) : <div className="mt-3"><Empty icon={Home} text="No public property listings yet." /></div>}
        </section>

        {/* BUYER NEEDS */}
        <section data-testid="section-buyer-needs">
          <h2 className="font-display text-lg font-bold text-[#0F2C59]">Active Buyer Needs {needs.total > 0 && <span className="text-slate-400 font-medium">({needs.total})</span>}</h2>
          {needs.items?.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-3">
              {needs.items.map((n) => <BuyerNeedCard key={n.id} n={n} />)}
            </div>
          ) : <div className="mt-3"><Empty icon={Users} text="No public buyer needs at the moment." /></div>}
        </section>

        {/* PROFESSIONAL INFORMATION */}
        <section data-testid="section-about">
          <h2 className="font-display text-lg font-bold text-[#0F2C59]">Professional Information</h2>
          <div className="mt-3 bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
            <Row label="Professional Role" value={pro.role_label} />
            {pro.sales_position_label && <Row label="Sales / Business Position" value={pro.sales_position_label} />}
            {(pro.organization_name || pro.agency) && <Row label="Organization" value={`${pro.organization_name || pro.agency}${pro.organization_branch ? ` — ${pro.organization_branch}` : ""}`} />}
            {pro.affiliation_status_label && <Row label="Affiliation" value={pro.affiliation_status_label} />}
            {areas.length > 0 && <Row label="Service Areas" value={areas.join(" • ")} />}
            <Row label="Verification" value={pro.is_verified ? "Verified Professional" : "Not yet verified"} verified={pro.is_verified} />
          </div>
        </section>

        {/* CONTACT */}
        <section data-testid="section-contact">
          <h2 className="font-display text-lg font-bold text-[#0F2C59]">Contact</h2>
          <div className="mt-3 bg-white border border-slate-200 rounded-2xl p-5">
            {hasChannels ? (
              <div className="space-y-3">
                <p className="text-[11px] uppercase tracking-wide text-slate-400">Direct business contact</p>
                <ContactChannels pro={pro} />
                {!isOwner && inquiryOn && (
                  <button onClick={() => setInquiryOpen(true)} data-testid="pro-contact-inquiry" className="text-sm font-semibold text-[#0F2C59] hover:text-[#C79A3B] inline-flex items-center gap-1 pt-1 mr-4">
                    <Send className="h-4 w-4" /> Send a private inquiry
                  </button>
                )}
                {!isOwner && !connDone && (
                  <button onClick={doInquiry} data-testid="pro-contact-workwithme" className="text-sm font-semibold text-[#C79A3B] hover:text-[#a97f28] inline-flex items-center gap-1 pt-1">
                    <Briefcase className="h-4 w-4" /> More ways to work together
                  </button>
                )}
              </div>
            ) : (
              <div>
                <p className="text-sm text-slate-500">{inquiryOn ? `Direct contact details are private. Send a secure inquiry — only ${pro.name} receives your details.` : "Direct contact details are private. Use the actions below to reach out."}</p>
                {!isOwner && (
                  <div className="flex flex-wrap gap-2.5 mt-3">
                    {inquiryOn && <Button onClick={() => setInquiryOpen(true)} data-testid="pro-inquiry-contact-send" className="rounded-xl bg-[#C79A3B] hover:bg-[#b98d33] text-[#0F2C59] font-semibold"><Send className="h-4 w-4 mr-1.5" />Send Inquiry</Button>}
                    <Button onClick={doConnect} disabled={connecting || connDone} className="rounded-xl bg-[#0F2C59] hover:bg-[#0F2C59]/90 text-white font-semibold"><ConnIcon className="h-4 w-4 mr-1.5" />{connLabel}</Button>
                    <Button onClick={doInquiry} variant="outline" data-testid="pro-inquiry-contact" className="rounded-xl border-[#0F2C59] text-[#0F2C59] font-semibold"><Briefcase className="h-4 w-4 mr-1.5" />Work With Me</Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </main>

      <SiteFooter variant="full" />
      <ShareMenu open={shareOpen} onOpenChange={setShareOpen} url={proShareUrl(pro.public_slug || slug)} name={pro.name} roleText={pro.sales_position_label || pro.role_label} />

      <ProInquiryDialog open={inquiryOpen} onOpenChange={setInquiryOpen} pro={pro} slug={pro.public_slug || slug}
        user={user} src={featPlacement ? qs.get("src") : null} cid={featPlacement ? featCid : null} />

      {/* WORK WITH ME */}
      <Dialog open={workOpen} onOpenChange={setWorkOpen}>
        <DialogContent data-testid="work-with-me-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-[#0F2C59]">Work with {pro.name}</DialogTitle>
            <DialogDescription>Choose how you'd like to reach out — {pro.name} represents buyers and properties on PropertyConnect.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {hasChannels && (
              <div className="space-y-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-400">Direct contact</p>
                <ContactChannels pro={pro} />
              </div>
            )}
            {!isOwner && (
              <div className="space-y-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-400">On PropertyConnect</p>
                {inquiryOn && <Button onClick={() => { setWorkOpen(false); setInquiryOpen(true); }} data-testid="work-send-inquiry" className="w-full rounded-xl bg-[#0F2C59] hover:bg-[#143163] text-white font-semibold">
                  <Send className="h-4 w-4 mr-1.5" /> Send a private inquiry
                </Button>}
                <Button onClick={() => { setWorkOpen(false); doConnect(); }} disabled={connecting || connDone} data-testid="work-connect" className="w-full rounded-xl bg-[#C79A3B] hover:bg-[#C79A3B]/90 text-white font-semibold">
                  <ConnIcon className="h-4 w-4 mr-1.5" />{connDone ? connLabel : user ? "Send Connection Request" : "Sign in to Connect"}
                </Button>
                <Button onClick={() => { setWorkOpen(false); doShare(); }} variant="outline" data-testid="work-share" className="w-full rounded-xl border-[#0F2C59] text-[#0F2C59] font-semibold">
                  <Share2 className="h-4 w-4 mr-1.5" /> Share this profile
                </Button>
              </div>
            )}
            {!hasChannels && (
              <p className="text-xs text-slate-500">This professional hasn't published a direct phone or email channel yet. Connect on PropertyConnect to start the conversation.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Featured-only highlight strip. Real, live profile data only — never fabricated achievements.
function ProfileHighlights({ pro, areas }) {
  const items = [
    { k: "props", v: pro.public_property_count || 0, l: "Active public listings" },
    { k: "needs", v: pro.public_buyer_need_count || 0, l: "Active buyer needs" },
    { k: "areas", v: areas.length, l: "Service areas" },
  ];
  return (
    <section data-testid="pro-highlights" aria-label="Profile highlights">
      <div className="grid grid-cols-3 gap-3 rounded-2xl bg-white p-4 ring-1 ring-[#C79A3B]/30 shadow-[0_10px_30px_-18px_rgba(15,44,89,0.4)]">
        {items.map((x) => (
          <div key={x.k} className="text-center" data-testid={`pro-highlight-${x.k}`}>
            <div className="font-display text-2xl font-bold text-[#0F2C59]">{x.v}</div>
            <div className="text-[11px] text-slate-500 leading-tight">{x.l}</div>
          </div>
        ))}
      </div>
      <p className="mt-1.5 text-[11px] text-slate-400">Based on live PropertyConnect profile data.</p>
    </section>
  );
}

function Row({ label, value, verified }) {
  return (
    <div className="flex items-start justify-between gap-4 px-5 py-3.5">
      <span className="text-xs uppercase tracking-wide text-slate-400 shrink-0">{label}</span>
      <span className="text-sm text-slate-800 text-right inline-flex items-center gap-1.5">
        {verified && <BadgeCheck className="h-4 w-4 text-success" />}{value}
      </span>
    </div>
  );
}
